import React, { createContext, useContext, useState, useCallback } from 'react';

export interface WishlistItem {
  id: string;
  name: string;
  styleNumber: string;
  price: number;
  color: string;
  image: string;
}

interface WishlistContextType {
  items: WishlistItem[];
  isOpen: boolean;
  openWishlist: () => void;
  closeWishlist: () => void;
  addItem: (item: WishlistItem) => void;
  removeItem: (id: string) => void;
  isInWishlist: (id: string) => boolean;
  toggleItem: (item: WishlistItem) => void;
  totalItems: number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const openWishlist = useCallback(() => setIsOpen(true), []);
  const closeWishlist = useCallback(() => setIsOpen(false), []);

  const addItem = useCallback((newItem: WishlistItem) => {
    setItems(prev => {
      if (prev.find(item => item.id === newItem.id)) return prev;
      return [...prev, newItem];
    });
  }, []);

  const removeItem = useCallback((id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const isInWishlist = useCallback((id: string) => {
    return items.some(item => item.id === id);
  }, [items]);

  const toggleItem = useCallback((item: WishlistItem) => {
    if (isInWishlist(item.id)) {
      removeItem(item.id);
    } else {
      addItem(item);
    }
  }, [isInWishlist, removeItem, addItem]);

  const totalItems = items.length;

  return (
    <WishlistContext.Provider value={{
      items,
      isOpen,
      openWishlist,
      closeWishlist,
      addItem,
      removeItem,
      isInWishlist,
      toggleItem,
      totalItems,
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) throw new Error('useWishlist must be used within WishlistProvider');
  return context;
};
